import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertCircle, Loader2, Stethoscope, Pill, ShoppingCart, Boxes, ClipboardList, Building2, Search,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single-URL login. The user_id prefix decides which auth path to take:
//   pharmacystores* → staff-login role=pharmacystores → /pharmacy-stores
//   storesadmin*    → staff-login role=storesadmin    → /stores-admin
//   pharmacy*       → staff-login role=pharmacy       → /pharmacy
//   purchase*       → staff-login role=purchase       → /purchase
//   anything else   → doctor-login                    → /dashboard
//
// IMPORTANT prefix order: `pharmacystores` MUST be checked BEFORE `pharmacy`
// (longest prefix wins) — `pharmacystores01`.startsWith('pharmacy') is also
// true, so a naive check would route a stores keeper to the Pharmacy portal.
function detectRole(userId) {
  const id = (userId || '').trim().toLowerCase();
  if (!id) return null;
  // Email → tenant admin / receptiondesk / doctor by email (role decided
  // server-side via POST /api/auth/login). UI uses the 'email' chrome so
  // it doesn't mis-brand a receptionist as "Doctor Login".
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(id)) return 'email';
  if (id.startsWith('pharmacystores')) return 'pharmacystores';
  if (id.startsWith('storesadmin')) return 'storesadmin';
  if (id.startsWith('pharmacy')) return 'pharmacy';
  if (id.startsWith('purchase')) return 'purchase';
  return 'doctor';
}

// Visual chrome per detected role — keeps the login screen feeling tailored
// to the user while staying on a single URL.
const ROLE_CHROME = {
  doctor: {
    title: 'Doctor Login', subtitle: 'Smart clinical consultation workflow',
    icon: Stethoscope, idLabel: 'Doctor ID', idPlaceholder: 'Enter your Doctor ID',
    accent: 'from-blue-800 via-blue-900 to-teal-900', btn: 'bg-blue-700 hover:bg-blue-800',
    badge: null,
  },
  // Email path: tenant admins, receptionists, doctors signing in by email.
  email: {
    title: 'Clinic Sign In', subtitle: 'Admin · Doctor · Reception Desk',
    icon: Building2, idLabel: 'Email address', idPlaceholder: 'you@clinic.com',
    accent: 'from-blue-800 via-blue-900 to-teal-900', btn: 'bg-blue-700 hover:bg-blue-800',
    badge: null,
  },
  pharmacy: {
    title: 'Pharmacy Login', subtitle: 'Dispensing & prescription fulfilment',
    icon: Pill, idLabel: 'Pharmacy User ID', idPlaceholder: 'e.g. pharmacy01',
    accent: 'from-emerald-700 via-emerald-800 to-teal-900', btn: 'bg-emerald-700 hover:bg-emerald-800',
    badge: { label: 'Pharmacy', cls: 'bg-emerald-500/20 text-emerald-100 border-emerald-300/30' },
  },
  purchase: {
    title: 'Purchase Login', subtitle: 'Vendor management & procurement',
    icon: ShoppingCart, idLabel: 'Purchase User ID', idPlaceholder: 'e.g. purchase01',
    accent: 'from-blue-800 via-blue-900 to-indigo-900', btn: 'bg-blue-700 hover:bg-blue-800',
    badge: { label: 'Purchase', cls: 'bg-blue-500/20 text-blue-100 border-blue-300/30' },
  },
  pharmacystores: {
    title: 'Pharmacy Stores Login', subtitle: 'Stockroom indents & requisitions',
    icon: Boxes, idLabel: 'Pharmacy Stores User ID', idPlaceholder: 'e.g. pharmacystores01',
    accent: 'from-amber-700 via-orange-800 to-amber-900', btn: 'bg-amber-700 hover:bg-amber-800',
    badge: { label: 'Pharmacy Stores', cls: 'bg-amber-500/20 text-amber-100 border-amber-300/30' },
  },
  storesadmin: {
    title: 'Stores Admin Login', subtitle: 'PR review & PO generation',
    icon: ClipboardList, idLabel: 'Stores Admin User ID', idPlaceholder: 'e.g. storesadmin01',
    accent: 'from-purple-800 via-violet-900 to-fuchsia-900', btn: 'bg-purple-700 hover:bg-purple-800',
    badge: { label: 'Stores Admin', cls: 'bg-purple-500/20 text-purple-100 border-purple-300/30' },
  },
};

function homeForRole(role) {
  // Mirror App.js → homeForRole so a fresh login lands on the role's
  // actual portal and ProtectedRoute doesn't bounce it back here.
  if (role === 'pharmacy' || role === 'pharmacist') return '/pharmacy';
  if (role === 'purchase') return '/purchase';
  if (role === 'pharmacystores') return '/pharmacy-stores';
  if (role === 'storesadmin') return '/stores-admin';
  if (role === 'receptiondesk') return '/reception/home';
  return '/dashboard';
}

export default function LoginPage() {
  const { setUser } = useAuth();
  const { slug, setSlug, availableTenants, tenant, pinned, platformConfig, platformReady } = useTenant();
  const branding = tenant?.branding || null;
  // When the tenant came from a subdomain or ?tenant= override, lock the
  // selector and render the tenant's branding. Otherwise show the picker.
  const useBranded = pinned && !!tenant;
  const brandColor = useBranded ? branding?.primary_color : null;
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantQuery, setTenantQuery] = useState('');

  // If the page is opened on a stale TenantContext (e.g. previous route was
  // /c/<slug> where there was no ?tenant=, leaving slug empty even though
  // the user has now navigated to /login?tenant=foo) — adopt the URL slug
  // so headers and auth work. Bare /login with no ?tenant= is handled by
  // the "Clinic Required" gate below, NOT by silently adopting a default.
  useEffect(() => {
    if (slug || pinned) return;
    const urlSlug = (new URLSearchParams(window.location.search).get('tenant') || '').toLowerCase();
    if (urlSlug) setSlug(urlSlug);
  }, [slug, pinned, setSlug]);

  // Dedupe by slug + filter by the user's search query. Slugs that have
  // never been used (custom subdomain) are surfaced by slug-match too.
  const filteredTenants = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of availableTenants) {
      if (seen.has(t.slug)) continue;
      seen.add(t.slug);
      out.push(t);
    }
    const q = tenantQuery.trim().toLowerCase();
    if (!q) return out;
    return out.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.slug || '').toLowerCase().includes(q)
    );
  }, [availableTenants, tenantQuery]);

  // Live-detect role as the user types so the chrome (color, label, icon)
  // reflects the destination portal. Stays on `doctor` until they start
  // typing a recognised prefix.
  const detectedRole = useMemo(() => detectRole(userId) || 'doctor', [userId]);
  const chrome = ROLE_CHROME[detectedRole] || ROLE_CHROME.doctor;
  const Icon = chrome.icon;

  // Gate: the login URL must arrive with an EXPLICIT tenant context, either
  // via the URL (`?tenant=<slug>`) or via a tenant subdomain that the
  // TenantContext pinned. A platform `default_tenant_slug` is intentionally
  // NOT enough — the user requires the slug to be visible in the link they
  // distribute. Without one of these the bare `/login` URL renders a
  // "Clinic required" gate instead of the login form.
  const urlHasTenantParam = useMemo(() => {
    try {
      return !!new URLSearchParams(window.location.search).get('tenant');
    } catch (_) { return false; }
  }, []);
  const tenantContextMissing = !pinned && !urlHasTenantParam;
  if (platformReady && tenantContextMissing) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-900 via-blue-950 to-slate-900"
        data-testid="login-tenant-required-gate"
      >
        <div className="w-full max-w-md text-center">
          <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-5 ring-2 ring-white/20 shadow-xl">
            <Building2 className="h-9 w-9 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-white mb-3"
              style={{ fontFamily: 'Manrope' }}>
            Clinic Required
          </h1>
          <p className="text-white/75 leading-relaxed mb-6">
            This sign-in page must be opened from your clinic's link.
            Please use the URL your clinic shared with you — it includes a
            <span className="font-mono text-white"> ?tenant=&lt;slug&gt; </span>
            tag or a clinic-specific subdomain.
          </p>
          <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 p-4 mb-6 text-left">
            <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">Example</p>
            <p className="font-mono text-sm text-white break-all">
              {window.location.origin}/login?tenant=<span className="text-emerald-300">your-clinic</span>
            </p>
          </div>
          <a
            href="/signup"
            className="inline-block px-5 py-2.5 rounded-lg bg-white text-blue-900 font-semibold text-sm hover:bg-white/90 transition"
            data-testid="login-gate-create-clinic"
          >
            Don't have a clinic? Create one →
          </a>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!slug) {
      setError('Please select your clinic / tenant first.');
      return;
    }
    setLoading(true);

    // If the user typed an email (which is how tenant admins are issued by
    // super-admin onboarding), use the email/password endpoint — this is
    // the only route that exercises the real bcrypt password and resolves
    // to a real "admin" role.
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userId.trim());
    const role = detectRole(userId);
    try {
      let data;
      if (isEmail) {
        const res = await axios.post(`${API_URL}/api/auth/login`, {
          email: userId.trim(),
          password: password,
        }, { withCredentials: true });
        data = res.data;
      } else if (role === 'doctor') {
        // Doctor login keeps its password requirement (any pw works per
        // dummy-auth posture, but we still need to send something).
        const res = await axios.post(`${API_URL}/api/auth/doctor-login`, {
          doctor_id: userId.trim(),
          password: password || 'any',
        }, { withCredentials: true });
        data = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/auth/staff-login`, {
          user_id: userId.trim(),
          role,
          password: password,
        }, { withCredentials: true });
        data = res.data;
      }
      setUser(data);
      // Tenant admins go to the admin portal; everyone else to their
      // role-derived home.
      const dest =
        (data.role || '').toLowerCase() === 'admin' ? '/admin/overview' : homeForRole(data.role);
      window.location.assign(dest);
    } catch (err) {
      let msg = 'Login failed. Please check your credentials.';
      if (!err.response) {
        msg = 'Cannot reach server. Check your network or try again shortly.';
      } else if (err.response.status === 401) {
        msg = 'Invalid credentials.';
      } else if (err.response.status === 404) {
        msg = 'Login service unavailable. The server may be updating — please retry in a minute.';
      } else if (err.response.data?.detail) {
        const d = err.response.data.detail;
        msg = typeof d === 'string' ? d : msg;
      }
      setError(msg);
    }
    setLoading(false);
  };

  // Doctor login is the only one that requires a password input (the staff
  // logins use dummy auth — but we keep the field visible so the form
  // shape stays consistent and doesn't jolt when the prefix is recognised).
  // Pharmacy & StoresAdmin are PROTECTED — they validate the password.
  // Email logins (admin/reception/doctor by email) always require a real
  // password.
  const isStaff = detectedRole !== 'doctor' && detectedRole !== 'email';
  const PROTECTED_ROLES = ['pharmacy', 'storesadmin'];
  const isPasswordRequired = detectedRole === 'email' || !isStaff || PROTECTED_ROLES.includes(detectedRole);
  const passwordPlaceholder =
    detectedRole === 'email' ? 'Your account password'
    : detectedRole === 'pharmacy' ? 'Enter pharmacy password'
    : detectedRole === 'storesadmin' ? 'Enter stores admin password'
    : isStaff ? 'staff123 (any)'
    : 'Password';

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 transition-colors duration-500 ${useBranded ? '' : `bg-gradient-to-br ${chrome.accent}`}`}
      style={useBranded ? {
        // Branded login — layered gradient with a subtle dot grid behind
        // the card for visual texture; switches to a clean tinted plane
        // for clinics with no primary color set.
        background: `
          radial-gradient(at 12% 18%, ${brandColor}44 0px, transparent 55%),
          radial-gradient(at 88% 82%, ${brandColor}33 0px, transparent 55%),
          linear-gradient(135deg, ${brandColor}1f 0%, ${brandColor}66 100%)
        `,
      } : undefined}
      data-testid="login-page"
    >
      {useBranded && (
        // Subtle dot pattern overlay only on branded login.
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
      )}
      <div className="w-full max-w-md relative z-10">
        {/* Logo / Branding */}
        <div className="text-center mb-8">
          <div
            className="h-20 w-20 rounded-2xl bg-white/80 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 overflow-hidden ring-2 ring-white/60 shadow-xl"
            style={useBranded ? { boxShadow: `0 18px 40px -12px ${brandColor}80` } : undefined}
            data-testid="login-brand-logo"
          >
            {useBranded && branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="h-16 w-16 object-contain" />
            ) : (
              <Icon className="h-8 w-8 text-white" />
            )}
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight"
              style={{ fontFamily: 'Manrope', color: useBranded ? brandColor : 'white' }}
              data-testid="login-brand-name">
            {useBranded ? (branding?.display_name || tenant?.name) : 'ECare 360'}
          </h1>
          {useBranded && branding?.tagline && (
            <p className="text-zinc-700/80 mt-1.5 text-sm italic max-w-sm mx-auto leading-snug"
               data-testid="login-brand-tagline">
              {branding.tagline}
            </p>
          )}
          {!useBranded && (
            <p className="text-white/80 mt-2 text-base">{chrome.subtitle}</p>
          )}
        </div>

        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-4">
            <CardTitle
              className="text-xl text-center flex items-center justify-center gap-2"
              style={{ fontFamily: 'Manrope' }}
              data-testid="login-detected-role-title"
            >
              {chrome.title}
              {chrome.badge && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${chrome.badge.cls.replace('text-', 'text-').replace(/text-[a-z]+-100/, (m) => m.replace('100', '700')).replace(/bg-[a-z]+-500\/20/, (m) => m.replace('500/20', '100')).replace(/border-[a-z]+-300\/30/, (m) => m.replace('300/30', '200'))}`}>
                  Auto-detected
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                  data-testid="login-error"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
                </div>
              )}

              <div className="space-y-2" hidden={useBranded}>
                <Label htmlFor="tenant-select" className="text-sm font-medium flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-zinc-500" />
                  Clinic / Tenant
                </Label>
                {availableTenants.length > 0 ? (
                  <Select value={slug || ''} onValueChange={setSlug}>
                    <SelectTrigger className="h-12" data-testid="login-tenant-select">
                      <SelectValue placeholder={`Select your clinic (${availableTenants.length})`} />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Search filter — sticky at top of the dropdown so
                          tenants with long lists stay scannable. */}
                      <div
                        className="sticky top-0 bg-white border-b p-2 z-10"
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                          <Input
                            value={tenantQuery}
                            onChange={(e) => setTenantQuery(e.target.value)}
                            placeholder="Search clinic name or slug…"
                            className="pl-8 h-8 text-xs"
                            data-testid="login-tenant-search"
                            autoFocus={false}
                          />
                        </div>
                      </div>
                      {filteredTenants.length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-zinc-500">
                          No clinics match "{tenantQuery}"
                        </div>
                      ) : filteredTenants.map((t) => (
                        <SelectItem key={t.slug} value={t.slug}>
                          <span className="block">{t.name}</span>
                          <span className="block text-[10px] text-zinc-500 font-mono">{t.slug}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="tenant-select"
                    value={slug || ''}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="acme-clinic"
                    className="h-12 text-base"
                    data-testid="login-tenant-input"
                  />
                )}
                {tenant?.status === 'suspended' && (
                  <p className="text-[11px] text-red-600">This tenant is currently suspended.</p>
                )}
                {slug && !tenant && (
                  <p className="text-[11px] text-amber-600">Tenant not found — check the slug.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="doctor-id" className="text-sm font-medium">{chrome.idLabel}</Label>
                <Input
                  id="doctor-id"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder={chrome.idPlaceholder}
                  className="h-12 text-base"
                  required
                  autoFocus
                  data-testid="login-doctor-id"
                />
                {isStaff && (
                  <p className="text-[11px] text-slate-500" data-testid="login-staff-hint">
                    Detected as <b>{chrome.title.replace(' Login', '')}</b>. Submit to enter.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={passwordPlaceholder}
                  className="h-12 text-base"
                  required={isPasswordRequired}
                  data-testid="login-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !userId.trim()}
                className={`w-full h-12 text-base ${useBranded ? '' : chrome.btn}`}
                style={useBranded ? { backgroundColor: brandColor } : undefined}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />Signing In...
                  </>
                ) : 'Sign In'}
              </Button>
            </form>
            {!useBranded && (
              <p className="text-center mt-5 text-xs text-zinc-500" data-testid="login-signup-link">
                Don't have a clinic yet?{' '}
                <a href="/signup" className="font-medium text-blue-700 hover:underline">
                  Create one in 60 seconds →
                </a>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Clinic footer — visible only when branded. Surfaces letterhead
            info (doctor + address + hours) so the login page actually
            *feels* like the clinic's, not generic SaaS chrome. */}
        {useBranded && (branding?.doctor_name || branding?.contact_address || branding?.consulting_hours) && (
          <div className="mt-6 text-center space-y-1.5 px-3"
               data-testid="login-clinic-footer">
            {branding.doctor_name && (
              <p className="text-sm font-semibold text-zinc-800" style={{ color: brandColor }}>
                {branding.doctor_name}
              </p>
            )}
            {branding.doctor_qualifications && (
              <p className="text-[11px] text-zinc-600 leading-relaxed max-w-md mx-auto">
                {branding.doctor_qualifications}
              </p>
            )}
            {branding.doctor_reg_no && (
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">
                Reg. No. {branding.doctor_reg_no}
              </p>
            )}
            {branding.contact_address && (
              <p className="text-[11px] text-zinc-600 mt-2 leading-relaxed max-w-md mx-auto">
                {branding.contact_address}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-zinc-600 mt-1.5">
              {branding.contact_phone && (
                <span>📞 {branding.contact_phone}</span>
              )}
              {branding.consulting_hours && (
                <span>🕒 {branding.consulting_hours}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
